/**
 * useTableView.js - the person's view of a table: which columns show and in
 * what order, the highlight rules, and their saved views (10/9/26). The
 * filters, sort and search stay the table's own state; a view snapshots and
 * restores them through the setters it is handed.
 *
 * Views are stored server-side (/api/table-views, routes/tableViews.js) so
 * they follow the person to any device. The built-in "Default" (every column,
 * the table's usual filters and sort, no highlights) always exists and is
 * never stored; the view marked as their default loads when the tab opens.
 * Without a tableKey (an account's Opportunities tab) nothing is fetched and
 * the table simply shows its default columns.
 *
 * keepFilters: the tab was opened from a deep link (#/projects?customer=X):
 * the default view's columns and highlights load, the link's filters stay.
 *
 * Sharing (round 4, 10/9/26): the list also holds views other people shared
 * with this person (mine: false) - read-only: they can be applied, copied
 * ("Save a copy as mine"), hidden, or opened first, never changed. The list
 * re-reads when a live 'table-views' event for this table arrives (the
 * server sends those only to the people concerned): an owner's update shows
 * at once, and a view unshared or deleted while in use falls back to Default.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveRefresh } from '../../live/useLive';
import { moveTo } from './usePointerSort';
import { DEFAULT_HOVER } from './highlights';
import { DEFAULT_TREE, cleanTree } from './treeLook';
import {
  resolveColumns,
  serializeFilters,
  deserializeFilters,
  sameConfig,
  cleanWidths,
  clampWidth,
  cleanFonts,
  clampFont,
  cleanLook,
  cleanAlign,
  cleanPinned,
  DEFAULT_LOOK,
  MAX_PINNED,
} from './viewConfig';

const API = '/api/table-views';

async function send(method, url, body) {
  const r = await fetch(url, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new Error((data && data.error) || 'Could not save the view - try again.');
  return data;
}

export function useTableView({
  tableKey,
  defs,
  columnFilters,
  setColumnFilters,
  sort,
  setSort,
  search,
  setSearch,
  defaultFilters,
  defaultSort,
  keepFilters = false,
  grouping = false,
}) {
  // the built-in Default, fixed for the life of the table
  const [builtIn] = useState(() => ({
    columns: defs.map((d) => d.col),
    hidden: [],
    filters: serializeFilters(defaultFilters || {}),
    sort: defaultSort,
    search: '',
    highlights: [],
  }));
  const EMPTY_LAYOUT = {
    hidden: [],
    highlights: [],
    widths: {},
    fonts: {},
    hover: DEFAULT_HOVER,
    align: {},
    pinned: [],
    look: DEFAULT_LOOK,
    tree: DEFAULT_TREE,
  };
  const [layout, setLayout] = useState({ columns: builtIn.columns, ...EMPTY_LAYOUT });
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [error, setError] = useState('');

  const { order, visible } = useMemo(() => resolveColumns(defs, layout), [defs, layout]);
  const current = useMemo(
    () => ({
      columns: order,
      hidden: layout.hidden,
      filters: serializeFilters(columnFilters),
      sort,
      search: search || '',
      highlights: layout.highlights,
      widths: layout.widths,
      fonts: layout.fonts,
      hover: layout.hover,
      align: layout.align,
      pinned: layout.pinned,
      look: layout.look,
      tree: layout.tree,
    }),
    [order, layout, columnFilters, sort, search]
  );
  const active = views.find((v) => v.id === activeId) || null;
  const mineList = views.filter((v) => v.mine !== false);
  const sharedList = views.filter((v) => v.mine === false);
  const newCount = sharedList.filter((v) => v.isNew && !v.hidden).length;
  // a view saved without a sort opens in the usual one (apply), so compare it that way
  const modified = !sameConfig(current, active ? { ...active.config, sort: active.config.sort || builtIn.sort } : builtIn, defs);

  const apply = useCallback(
    (config, { layoutOnly = false } = {}) => {
      const c = config || {};
      setLayout({
        columns: c.columns && c.columns.length ? c.columns : builtIn.columns,
        hidden: c.hidden || [],
        highlights: c.highlights || [],
        widths: cleanWidths(defs, c.widths),
        fonts: cleanFonts(defs, c.fonts),
        hover: { ...DEFAULT_HOVER, ...(c.hover || {}) },
        align: cleanAlign(defs, c.align),
        pinned: cleanPinned(defs, c.pinned),
        look: cleanLook(c.look),
        tree: cleanTree(c.tree),
      });
      if (layoutOnly) return;
      setColumnFilters(deserializeFilters(c.filters));
      setSort(c.sort || builtIn.sort);
      setSearch(c.search || '');
    },
    [builtIn, defs, setColumnFilters, setSort, setSearch]
  );

  // The person's views for this table (and those shared with them); their
  // default one applies on arrival.
  const keepRef = useRef(keepFilters);
  const read = useCallback(
    () =>
      Promise.resolve()
        .then(() => fetch(`${API}?table=${encodeURIComponent(tableKey)}`))
        .then((r) => (r && r.ok ? r.json() : null))
        .then((list) => (Array.isArray(list) ? list.filter((v) => v && v.id != null && v.config) : null))
        .catch(() => null),
    [tableKey]
  );
  useEffect(() => {
    if (!tableKey) return undefined;
    let live = true;
    read().then((list) => {
      if (!live || !list) return; // no views: the table shows its Default
      if (list.length) setViews(list);
      const def = list.find((v) => v.isDefault && !v.hidden);
      if (def) {
        setActiveId(def.id);
        apply(def.config, { layoutOnly: keepRef.current });
      }
    });
    return () => {
      live = false;
    };
  }, [tableKey, apply, read]);

  // A re-read after someone shared, updated, unshared or deleted a view. The
  // view on screen: gone -> back to Default; changed by its owner while this
  // person had not touched it -> the new version is applied.
  const stateRef = useRef({});
  stateRef.current = { views, activeId, modified };
  const reload = useCallback(
    () =>
      read().then((list) => {
        if (!list) return;
        const { views: was, activeId: on, modified: dirty } = stateRef.current;
        setViews(list);
        if (on == null) return;
        const now = list.find((v) => v.id === on && !v.hidden);
        if (!now) {
          setActiveId(null);
          apply(builtIn);
          return;
        }
        const before = was.find((v) => v.id === on);
        if (!dirty && before && JSON.stringify(before.config) !== JSON.stringify(now.config)) apply(now.config);
      }),
    [read, apply, builtIn]
  );
  useLiveRefresh('table-views', () => (tableKey ? reload() : null), { match: (e) => e.sub === tableKey });

  const run = async (fn) => {
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
      return null;
    }
  };

  const selectView = (id) => {
    setError('');
    const v = views.find((x) => x.id === id) || null;
    setActiveId(v ? v.id : null);
    apply(v ? v.config : builtIn);
  };

  const saveAs = (name, makeDefault = false) =>
    run(async () => {
      const v = await send('POST', API, { table: tableKey, name, config: current, isDefault: makeDefault });
      setViews((list) => [...list.map((x) => (makeDefault ? { ...x, isDefault: false } : x)), v]);
      setActiveId(v.id);
      return v;
    });

  const update = () =>
    active &&
    run(async () => {
      const v = await send('PUT', `${API}/${active.id}`, { config: current });
      setViews((list) => list.map((x) => (x.id === v.id ? v : x)));
      return v;
    });

  const rename = (id, name) =>
    run(async () => {
      const v = await send('PUT', `${API}/${id}`, { name });
      setViews((list) => list.map((x) => (x.id === v.id ? v : x)));
      return v;
    });

  const remove = (id) =>
    run(async () => {
      await send('DELETE', `${API}/${id}`);
      setViews((list) => list.filter((x) => x.id !== id));
      if (id === activeId) {
        setActiveId(null);
        apply(builtIn);
      }
      return true;
    });

  // id = the view to open on arrival (theirs or one shared with them); null =
  // the built-in Default. Per person on the server: never the owner's.
  const setDefault = (id) =>
    run(async () => {
      const was = views.find((x) => x.isDefault);
      if (id == null) {
        if (was) await send('PUT', `${API}/${was.id}/me`, { isDefault: false });
      } else {
        await send('PUT', `${API}/${id}/me`, { isDefault: true });
      }
      setViews((list) => list.map((x) => ({ ...x, isDefault: x.id === id })));
      return true;
    });

  // ── sharing (round 4) ──
  // who an own view is shared with: emails, and/or everyone; both empty = stop
  const share = (id, emails, everyone = false) =>
    run(async () => {
      const r = await send('PUT', `${API}/${id}/shares`, { emails, everyone });
      setViews((list) => list.map((x) => (x.id === id ? { ...x, sharedWith: r.sharedWith } : x)));
      return r;
    });
  // "Save a copy as mine": a shared view becomes one of their own, and is shown
  const copy = (id) =>
    run(async () => {
      const v = await send('POST', `${API}/${id}/copy`, {});
      setViews((list) => [...list, v]);
      setActiveId(v.id);
      apply(v.config);
      return v;
    });
  // hide a shared view from their list (or bring it back)
  const hide = (id, hidden = true) =>
    run(async () => {
      await send('PUT', `${API}/${id}/me`, { hidden });
      setViews((list) => list.map((x) => (x.id === id ? { ...x, hidden, isDefault: hidden ? false : x.isDefault } : x)));
      if (hidden && id === activeId) {
        setActiveId(null);
        apply(builtIn);
      }
      return true;
    });
  // the menu was opened: shared views are no longer New
  const markSeen = () => {
    if (!newCount) return;
    setViews((list) => list.map((x) => (x.mine === false ? { ...x, isNew: false } : x)));
    send('POST', `${API}/seen`, { table: tableKey }).catch(() => {});
  };

  const revert = () => apply(active ? active.config : builtIn);

  // ── columns ──
  const toggleColumn = (col) =>
    setLayout((l) => {
      const hidden = l.hidden.includes(col) ? l.hidden.filter((c) => c !== col) : [...l.hidden, col];
      if (order.every((c) => hidden.includes(c))) return l; // keep one column showing
      return { ...l, columns: order, hidden };
    });
  const moveColumn = (col, delta) =>
    setLayout((l) => {
      const next = [...order];
      const i = next.indexOf(col);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= next.length) return l;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...l, columns: next };
    });
  // pointer drag (usePointerSort): `to` is the slot in the full list
  // ('all', the Customize list) or among the columns on screen ('visible',
  // the headers - the hidden ones keep their places around them)
  const reorder = (col, to, scope = 'all') =>
    setLayout((l) => {
      if (scope === 'all') return { ...l, columns: moveTo(order, col, to) };
      const shown = visible.map((d) => d.col);
      const next = moveTo(shown, col, to);
      let k = 0;
      return { ...l, columns: order.map((c) => (shown.includes(c) ? next[k++] : c)) };
    });
  const selectAll = () => setLayout((l) => ({ ...l, columns: order, hidden: [] }));
  // Unselect all keeps the FIRST column in the list showing - a table is never empty
  const unselectAll = () => setLayout((l) => ({ ...l, columns: order, hidden: order.slice(1) }));
  // widths: px per column, clamped; a reset goes back to each column's own width
  // The first resize also records every other column as it was on screen
  // (`measured`, px by column), so the table does not jump when it switches
  // to fixed widths (viewConfig.tableLayout).
  const setWidth = (col, px, measured) =>
    setLayout((l) => {
      const base = Object.keys(l.widths).length || !measured ? l.widths : cleanWidths(defs, measured);
      return { ...l, widths: { ...base, [col]: clampWidth(px) } };
    });
  const clearWidth = (col) =>
    setLayout((l) => {
      const w = { ...l.widths };
      delete w[col];
      return { ...l, widths: w };
    });
  const resetWidths = () => setLayout((l) => ({ ...l, widths: {} }));
  // font sizes: which = 'head' | 'cell'; px null puts it back to the table's own
  const setFont = (col, which, px) =>
    setLayout((l) => {
      const one = { ...(l.fonts[col] || {}) };
      if (px == null || px === '') delete one[which];
      else one[which] = clampFont(px);
      const fonts = { ...l.fonts };
      if (one.head || one.cell) fonts[col] = one;
      else delete fonts[col];
      return { ...l, fonts };
    });
  const resetFonts = () => setLayout((l) => ({ ...l, fonts: {} }));
  // row hover: { on?, color? }
  const setHover = (patch) => setLayout((l) => ({ ...l, hover: { ...l.hover, ...patch } }));
  const resetColumns = () => setLayout((l) => ({ ...l, columns: builtIn.columns, hidden: [] }));
  // ── round 4: the Customize revamp ──
  // a column's alignment: 'left' | 'center' | 'right', null = its own
  const setAlign = (col, a) =>
    setLayout((l) => {
      const align = { ...l.align };
      if (a) align[col] = a;
      else delete align[col];
      return { ...l, align };
    });
  // freeze a column at the left (it moves up to join the frozen ones) or let it go
  const togglePin = (col) =>
    setLayout((l) => {
      if (l.pinned.includes(col)) return { ...l, pinned: l.pinned.filter((c) => c !== col) };
      if (l.pinned.length >= MAX_PINNED) return l;
      const pinned = [...l.pinned, col];
      const rest = order.filter((c) => !pinned.includes(c));
      return { ...l, pinned, columns: [...order.filter((c) => pinned.includes(c)), ...rest], hidden: l.hidden.filter((c) => c !== col) };
    });
  // the look: density, font size, zebra, gridlines, header, wrap, totals, sticky header
  const setLook = (patch) => setLayout((l) => ({ ...l, look: cleanLook({ ...l.look, ...patch }) }));
  // the main project / sub-project rows (treeLook.js): setTree({ lines }) or setTree({ main: { fill } })
  const setTree = (patch) =>
    setLayout((l) => {
      const cur = cleanTree(l.tree);
      const next = { ...cur, ...patch };
      for (const lv of ['main', 'sub']) if (patch[lv]) next[lv] = { ...cur[lv], ...patch[lv] };
      return { ...l, tree: cleanTree(next) };
    });
  // one-click resets, per Customize section
  const resetSection = (section) =>
    setLayout((l) => {
      switch (section) {
        case 'columns':
          return { ...l, columns: builtIn.columns, hidden: [], widths: {}, fonts: {}, align: {}, pinned: [] };
        case 'highlights':
          return { ...l, highlights: [] };
        case 'appearance':
          return {
            ...l,
            hover: DEFAULT_HOVER,
            look: { ...l.look, density: DEFAULT_LOOK.density, fontSize: null, zebra: true, gridlines: true, header: 'band', wrap: true },
          };
        case 'tree':
          return { ...l, tree: DEFAULT_TREE };
        case 'rows':
          return { ...l, look: { ...l.look, totals: DEFAULT_LOOK.totals, sticky: DEFAULT_LOOK.sticky, group: DEFAULT_LOOK.group } };
        default:
          return l;
      }
    });
  const resetRows = () => {
    resetSection('rows');
    setSort(builtIn.sort);
  };
  // "Reset everything": every setting of the view back to Default's (the filters and search stay)
  const resetAll = () => {
    setLayout({ columns: builtIn.columns, ...EMPTY_LAYOUT });
    setSort(builtIn.sort);
  };
  const setHighlights = (highlights) => setLayout((l) => ({ ...l, highlights }));

  return {
    enabled: !!tableKey,
    // the table can group records under their main project (look.group, 10/14/26)
    grouping: !!grouping,
    order,
    visible,
    hidden: layout.hidden,
    highlights: layout.highlights,
    widths: layout.widths,
    fonts: layout.fonts,
    hover: layout.hover,
    align: layout.align,
    pinned: layout.pinned,
    look: layout.look,
    tree: cleanTree(layout.tree),
    sort,
    setSort,
    defaultSort: builtIn.sort,
    views,
    mineViews: mineList,
    sharedViews: sharedList,
    newCount,
    active,
    modified,
    error,
    clearError: () => setError(''),
    selectView,
    saveAs,
    update,
    rename,
    remove,
    setDefault,
    revert,
    share,
    copy,
    hide,
    markSeen,
    reload,
    toggleColumn,
    moveColumn,
    reorder,
    selectAll,
    unselectAll,
    resetColumns,
    setHighlights,
    setWidth,
    clearWidth,
    resetWidths,
    setFont,
    resetFonts,
    setHover,
    setAlign,
    togglePin,
    setLook,
    setTree,
    resetSection,
    resetRows,
    resetAll,
  };
}
